<template>
  <v-dialog v-model="dialog" width="900" persistent scrollable>
    <v-card>
      <AdCardTitle dialog>{{ mode === 'add' ? $t('add-scenario') : $t('edit-scenario') }}</AdCardTitle>
      <v-card-text class="pa-0">
        <v-stepper v-model="stepIndex" class="elevation-0">
          <v-stepper-header>
            <v-stepper-step :complete="stepIndex > 1" step="1">
              {{ $t('scenario') }}
            </v-stepper-step>
            <v-divider/>
            <v-stepper-step :complete="stepIndex > 2" step="2">
              {{ $t('transaction-group') }}
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-form ref="formStep1" @submit.prevent="goToStep2">
                <EditableTranslationEditor
                  v-model="newScenario.name"
                  ref="name"
                  :rules="rulesScenario.name"
                  :required="true"
                  :label="$t('name')"
                  :locales="projectLocales"
                />
                <EditableTranslationEditor
                  v-model="newScenario.desc"
                  :editor="true"
                  ref="desc"
                  :label="$t('description')"
                  :locales="projectLocales"
                />
              </v-form>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-form ref="formStep2" @submit.prevent="save">
                <template v-for="(transactionGroup, transactionGroupIndex) of newScenario.transaction">
                  <div
                    :key="transactionGroupIndex"
                    :class="getTransactionBoxColor('group', transactionGroup)"
                    class="mb-6 px-6 py-3 rounded-lg"
                  >
                    <div class="d-flex flex-row align-center">
                      <div class="flex-grow-1">
                        <v-row no-gutters>
                          <v-col cols="3">
                            <EditableDropdown
                              v-model="transactionGroup.type"
                              :prependIcon="$filters.getScenarioIcon('group')"
                              :items="transactionGroupTypes"
                              item-key="value"
                              item-text="label"
                              :label="$t('type')"
                              value="group"
                              disabled
                              @change="markAsEdit(transactionGroup)"
                            />
                          </v-col>
                          <v-col cols="9">
                            <EditableTranslationEditor
                              v-model="transactionGroup.name"
                              ref="name"
                              :rules="rulesScenario.name"
                              :required="true"
                              :label="$t('name')"
                              :locales="projectLocales"
                              @change="markAsEdit(transactionGroup)"
                            />
                          </v-col>
                        </v-row>
                      </div>
                      <div class="flex-grow-0">
                        <v-btn
                          :disabled="newScenario.transaction.length <= 1 || !!transactionGroup.id"
                          icon
                          @click="removeTransactionGroup(transactionGroup)"
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <template v-for="(transactionLeaf, transactionLeafIndex) of transactionGroup.transaction">
                      <div
                        :key="transactionLeafIndex"
                        :class="getTransactionBoxColor('leaf', transactionLeaf)"
                        class="mb-3 mx-n3 pa-3 rounded-lg"
                      >
                        <div class="d-flex flex-row align-start">
                          <div class="flex-grow-1">
                            <v-row no-gutters>
                              <v-col cols="3">
                                <EditableDropdown
                                  v-model="transactionLeaf.type"
                                  :prependIcon="$filters.getScenarioIcon(transactionLeaf.type)"
                                  :items="transactionLeafTypes"
                                  item-key="value"
                                  item-text="label"
                                  :label="$t('type')"
                                  value="group"
                                  @change="setType(transactionLeaf, $event)"
                                />
                              </v-col>
                              <v-col cols="9">
                                <EditableTranslationEditor
                                  v-model="transactionLeaf.name"
                                  ref="name"
                                  :rules="rulesScenario.name"
                                  :required="true"
                                  :label="$t('name')"
                                  :locales="projectLocales"
                                  @change="markAsEdit(transactionLeaf)"
                                />
                              </v-col>
                            </v-row>

                            <v-row no-gutters>
                              <v-col :cols="transactionLeaf.type !== 'stationary' ? 6 : 12">
                                <EditableDropdown
                                  v-model="transactionLeaf.actors.sender"
                                  :prependIcon="getActorIcon(transactionLeaf.actors.sender)"
                                  :items="actors"
                                  item-value="id"
                                  item-text="name"
                                  :required="true"
                                  :label="$t('actorRolesender')"
                                  @change="setActor('sender', transactionLeaf, $event)"
                                />
                              </v-col>
                              <v-col
                                v-if="transactionLeaf.type !== 'stationary'"
                                cols="6"
                              >
                                <EditableDropdown
                                  v-model="transactionLeaf.actors.receiver"
                                  :prependIcon="getActorIcon(transactionLeaf.actors.receiver)"
                                  :items="actors"
                                  item-value="id"
                                  item-text="name"
                                  :required="true"
                                  :label="$t('actorRolereceiver')"
                                  @change="setActor('receiver', transactionLeaf, $event)"
                                />
                              </v-col>
                            </v-row>
                          </div>
                          <div class="flex-grow-0 mt-4">
                            <v-btn
                              :disabled="transactionGroup.transaction.length <= 1 || !!transactionLeaf.id"
                              icon
                              @click="removeTransactionLeaf(transactionGroup, transactionLeaf)"
                            >
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </template>

                    <div class="mt-3 mx-n3">
                      <AdButton
                        type="add"
                        text="transaction leaf"
                        @click="addTransactionLeaf(transactionGroup)"
                      />
                    </div>
                  </div>
                </template>

                <div class="mt-3 mx-n3">
                  <AdButton
                    type="add"
                    text="transaction group"
                    @click="addTransactionGroup"
                  />
                </div>
              </v-form>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions class="dialogbottomstrip">
        <v-spacer/>
        <AdButton
          type="cancel"
          @click="cancel"
          :disabled="disabled || isLoading"
        />
        <AdButton
          v-if="stepIndex === 2"
          :text="$t('previous')"
          @click="goToStep1"
          :disabled="disabled || isLoading"
        />
        <AdButton
          v-if="stepIndex === 1"
          type="next"
          :text="$t('next')"
          @click="goToStep2"
          :disabled="disabled || isLoading"
        />
        <AdButton
          v-if="stepIndex === 2"
          type="save"
          :text="$t('save')"
          @click="save"
          :disabled="disabled || isLoading"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import rulesScenario from '@/rules/scenario'
import LockService from '@/services/LockService'
import { mapState } from 'vuex'

export default {
  name: 'ScenarioAddEditDialog',

  data () {
    return {
      dialog: false,
      mode: 'add',
      stepIndex: 1,
      disabled: false,
      isLoading: false,
      rulesScenario,
      newScenario: null
    }
  },

  computed: {
    ...mapState('scenario', ['actors']),

    newTransactionGroup () {
      return {
        type: 'group',
        name: [],
        transaction: [{
          type: 'initial',
          name: [],
          actors: {
            sender: null,
            receiver: null
          }
        }]
      }
    },

    newTransactionLeaf () {
      return {
        type: 'initial',
        name: [],
        actors: {
          sender: null,
          receiver: null
        }
      }
    },

    transactionGroupTypes () {
      return this.$store.getters['server/getTransactionTypes']
        .filter((type) => type.value === 'group')
    },

    transactionLeafTypes () {
      return this.$store.getters['server/getTransactionTypes']
        .filter((type) => type.value !== 'group')
    }
  },

  created () {
    this.initializeNewScenario()
  },

  methods: {
    getActorIcon (actorId) {
      const actor = this.actors.find((actor) => actor.id === actorId)
      return this.$filters.getActorIcon(actor?.type) || 'mdi-account-cog'
    },

    setType (transaction, type) {
      // Mark as edit.
      this.markAsEdit(transaction)
      // Check if type is stationary.
      if (type === 'stationary') {
        transaction.actors.receiver = null
      }
    },

    setActor (role, transaction, id) {
      // Mark as edit.
      this.markAsEdit(transaction)

      // Remove receiver id if it is the same as sender.
      if (role === 'sender' && transaction.actors.receiver === id) {
        transaction.actors.receiver = null
      }

      // Remove sender id if it is the same as receiver.
      if (role === 'receiver' && transaction.actors.sender === id) {
        transaction.actors.sender = null
      }
    },

    getTransactionBoxColor (type, item) {
      if (type === 'group') {
        return this.mode === 'edit' && !item?.id
          ? 'orange lighten-5'
          : 'grey lighten-5'
      }

      if (type === 'leaf') {
        return this.mode === 'edit' && !item?.id
          ? 'orange lighten-4'
          : 'grey lighten-3'
      }

      return ''
    },

    initializeNewScenario () {
      // Reset new scenario.
      this.newScenario = {
        name: [],
        desc: [],
        transaction: [{
          type: 'group',
          name: [],
          transaction: [{
            type: 'initial',
            name: [],
            actors: {
              sender: null,
              receiver: null
            }
          }]
        }]
      }
    },

    addTransactionGroup () {
      this.newScenario.transaction.push(
        this.$filters.clone(this.newTransactionGroup)
      )
    },

    addTransactionLeaf (transactionGroup) {
      transactionGroup.transaction.push(
        this.$filters.clone(this.newTransactionLeaf)
      )
    },

    removeTransactionGroup (transactionGroup) {
      const index = this.newScenario.transaction.indexOf(transactionGroup)
      if (index >= 0) {
        this.newScenario.transaction.splice(index, 1)
      }
    },

    removeTransactionLeaf (transactionGroup, transactionLeaf) {
      const index = transactionGroup.transaction.indexOf(transactionLeaf)
      if (index >= 0) {
        transactionGroup.transaction.splice(index, 1)
      }
    },

    goToStep1 () {
      this.stepIndex = 1
    },

    goToStep2 () {
      if (
        this.$refs.formStep1.validate() &&
        this.stepIndex === 1 &&
        !this.disabled &&
        !this.isLoading
      ) {
        this.stepIndex = 2
      }
    },

    async cancel () {
      // Get object id.
      const objectId = this.newScenario?.id || null
      const effectiveDate = this.newScenario?.effectiveDate
      // Check if object id is not null.
      if (objectId !== null) {
        // Try to unlock object by id and get response.
        await LockService.unlockObjectId({ objectId, effectiveDate })
          .then(async () => {
            // Close dialog.
            this.close()
          })
          .catch((error) => {
            // Get error message.
            const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
            // Show error message on alert dialog.
            this.$oops(errorMessage)
          })
      } else {
        // Close dialog.
        this.close()
      }
    },

    close () {
      this.dialog = false
    },

    open () {
      this.dialog = true

      if (this.$refs?.formStep1?.resetValidation instanceof Function) {
        this.$refs.formStep1.resetValidation()
      }

      if (this.$refs?.formStep2?.resetValidation instanceof Function) {
        this.$refs.formStep2.resetValidation()
      }
    },

    openAndAdd () {
      this.mode = 'add'
      this.stepIndex = 1
      this.initializeNewScenario()
      this.open()
    },

    parseEditableScenario (scenario) {
      return {
        ...scenario,
        transaction: scenario.transaction.map((transactionGroup) => ({
          ...transactionGroup,
          transaction: transactionGroup.transaction.map((transactionLeaf) => ({
            ...transactionLeaf,
            actors: {
              sender: transactionLeaf.actors[0].actor.find((actor) => actor.role === 'sender')?.id || null,
              receiver: transactionLeaf.actors[0].actor.find((actor) => actor.role === 'receiver')?.id || null
            }
          }))
        }))
      }
    },

    parseSavableScenario (scenario) {
      return {
        ...scenario,
        transaction: scenario.transaction.map((transactionGroup) => ({
          ...transactionGroup,
          transaction: transactionGroup.transaction.map((transactionLeaf) => ({
            ...transactionLeaf,
            actors: [{
              actor: [
                { role: 'sender', id: transactionLeaf.actors.sender },
                { role: 'receiver', id: transactionLeaf.actors.receiver }
              ].filter((actor) => actor.id)
            }]
          }))
        }))
      }
    },

    async openAndEdit ({ type, mode, item, target, scenario }) {
      this.mode = 'edit'
      this.stepIndex = 2
      console.log(scenario)
      this.newScenario = {
        ...this.parseEditableScenario(this.$filters.clone(scenario)),
        edit: [{
          mode: 'edit'
        }]
      }

      // Define mode mappings.
      const modeMappings = {
        preceding: 'addItemBeforeListItem',
        following: 'addItemAfterListItem',
        into: 'pushItemInsideListItem'
      }

      // Get list helper function.
      const listHelperFunction = modeMappings?.[mode]

      // Check if list helper function was found.
      if (listHelperFunction) {
        // Apply new item to scenario structure.
        this.newScenario = this.$filters.listHelper?.[listHelperFunction]({
          items: [this.newScenario],
          item: type === 'group'
            ? this.$filters.clone(this.newTransactionGroup)
            : this.$filters.clone(this.newTransactionLeaf),
          children: 'transaction',
          handler: (match) => match.id === item.id
        })[0]
      }

      // Get object id.
      const objectId = this.newScenario?.id || null
      // Get effective date.
      const effectiveDate = this.newScenario?.effectiveDate || null
      // Check if object id is not null.
      if (objectId !== null) {
        // Try to lock object by id and get response.
        await LockService.lockObjectId({ objectId, effectiveDate })
          .then(async () => {
            // Open dialog.
            this.open()
          })
          .catch((error) => {
            // Get error message.
            const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
            // Show error message on alert dialog.
            this.$oops(errorMessage)
          })
      }
    },

    markAsEdit (transaction) {
      // Check if edit mode is active.
      if (this.mode === 'edit') {
        // Add edit mode property on transaction.
        transaction.edit = [{ mode: 'edit' }]
      }
    },

    save () {
      if (
        this.$refs.formStep2.validate() &&
        this.stepIndex === 2 &&
        !this.disabled &&
        !this.isLoading
      ) {
        const newScenario = this.parseSavableScenario(this.newScenario)
        if (this.mode === 'add') {
          return this.$store.dispatch('scenario/createScenarioItem', newScenario)
            .then((response) => {
              if (response?.data) {
                this.close()
                this.$emit('addScenarioFinish', (open) ? response.data : null)
              }
            })
            .catch((error) => {
              const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
              // Show error message on alert dialog.
              this.$oops(errorMessage)
            })
        } else {
          return this.$store.dispatch('scenario/updateScenarioItem', { item: newScenario, data: newScenario })
            .then((response) => {
              if (response) {
                this.close()
              }
            })
            .catch((error) => {
              const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
              // Show error message on alert dialog.
              this.$oops(errorMessage)
            })
        }
      }
    }
  }
}
</script>
